import React, { useEffect, useRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Animated, Platform, Image } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useTheme } from '../contexts/ThemeContext';
import { getColors, typography, spacing, borderRadius, getShadows } from '../styles/theme';
import { getProfilePictureUrl } from '../services/storage';

const ChatNotification = ({ notification, visible, onDismiss, onPress, index = 0, navigationRef }) => {
  const { isDarkMode } = useTheme();
  const colors = getColors(isDarkMode);
  const shadows = getShadows(isDarkMode);
  const slideAnim = useRef(new Animated.Value(-120)).current;
  const opacityAnim = useRef(new Animated.Value(0)).current;
  const styles = getStyles(colors, shadows, isDarkMode);

  // Calculate top position based on index (stack notifications)
  const topOffset = 20 + (index * 100); // 20px base + 100px per notification

  useEffect(() => {
    if (visible) {
      // Slide in and fade in
      Animated.parallel([
        Animated.spring(slideAnim, {
          toValue: 0,
          tension: 50,
          friction: 7,
          useNativeDriver: true,
        }),
        Animated.timing(opacityAnim, {
          toValue: 1,
          duration: 300,
          useNativeDriver: true,
        }),
      ]).start();

      // Auto dismiss after 5 seconds
      const timer = setTimeout(() => {
        handleDismiss();
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [visible]);

  const handleDismiss = () => {
    Animated.parallel([
      Animated.timing(slideAnim, {
        toValue: -120,
        duration: 200,
        useNativeDriver: true,
      }),
      Animated.timing(opacityAnim, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }),
    ]).start(() => {
      onDismiss();
    });
  };

  const handlePress = () => {
    // Navigate to Chat tab using ref (we're rendered outside the screen tree). React Navigation v5: ref.current
    try {
      if (navigationRef?.current && notification?.conversationId) {
        navigationRef.current.navigate('Chat', {
          openUserId: notification.senderId,
          conversationId: notification.conversationId,
        });
      } else if (onPress) {
        onPress(notification);
      }
    } catch (err) {
      console.warn('ChatNotification: navigation failed', err);
      if (onPress) onPress(notification);
    }
    handleDismiss();
  };

  if (!visible || !notification) return null;

  const profilePictureUrl = getProfilePictureUrl(notification.senderAvatar, 'thumbnail');
  const messagePreview = notification.message.length > 50 
    ? notification.message.substring(0, 50) + '...'
    : notification.message;

  return (
    <Animated.View
      style={[
        styles.container,
        {
          top: topOffset,
          transform: [{ translateY: slideAnim }],
          opacity: opacityAnim,
        },
      ]}
    >
      <TouchableOpacity
        style={styles.content}
        onPress={handlePress}
        activeOpacity={0.9}
      >
        {/* Profile Picture */}
        <View style={styles.avatarContainer}>
          {profilePictureUrl ? (
            <Image
              source={{ uri: profilePictureUrl }}
              style={styles.avatar}
              resizeMode="cover"
            />
          ) : (
            <View style={[styles.avatar, styles.avatarPlaceholder]}>
              <Icon name="person" size={20} color={colors.textLight} />
            </View>
          )}
        </View>

        {/* Message Content */}
        <View style={styles.messageContainer}>
          <View style={styles.headerRow}>
            <Text style={styles.senderName} numberOfLines={1}>
              {notification.senderName}
            </Text>
            <Text style={styles.timestamp}>Just now</Text>
          </View>
          <Text style={styles.messagePreview} numberOfLines={2}>
            {messagePreview}
          </Text>
        </View>

        {/* Close Button */}
        <TouchableOpacity
          onPress={handleDismiss}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          style={styles.closeButton}
        >
          <Icon name="close" size={18} color={colors.textSecondary} />
        </TouchableOpacity>
      </TouchableOpacity>
    </Animated.View>
  );
};

const getStyles = (colors, shadows, isDarkMode) => StyleSheet.create({
  container: {
    position: Platform.OS === 'web' ? 'fixed' : 'absolute',
    left: 20,
    right: 20,
    zIndex: 10000,
    borderRadius: borderRadius.md,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    ...shadows.medium,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    gap: spacing.sm,
  },
  avatarContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    overflow: 'hidden',
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
  },
  avatarPlaceholder: {
    backgroundColor: colors.backgroundDark,
    justifyContent: 'center',
    alignItems: 'center',
  },
  messageContainer: {
    flex: 1,
    gap: spacing.xs,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  senderName: {
    fontSize: typography.body,
    fontWeight: typography.semiBold,
    color: colors.text,
    flex: 1,
  },
  timestamp: {
    fontSize: typography.caption,
    color: colors.textSecondary,
  },
  messagePreview: {
    fontSize: typography.bodySmall,
    color: colors.textSecondary,
    lineHeight: typography.bodySmall * 1.4,
  },
  closeButton: {
    padding: spacing.xs,
  },
});

export default ChatNotification;
